/**
 * PublicReportView — read-only ProgressReport for /r/:token page (SCR-029).
 * No auth required. Comment list read-only. "Gửi phản hồi" → /login.
 * noindex meta tag. Brand CLC chung.
 */
import { useEffect } from 'react';
import { ReportComments } from './report-comments';
import { SessionReviewStatsCard } from './session-review-stats-card';
import type { PortalComment } from '@client/lib/queries/use-portal';
import type { SessionReviewStats } from '@client/lib/queries/use-progress-reports';

interface PublicReport {
  studentName: string;
  className: string;
  courseName?: string;
  periodLabel?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  attendanceSummary?: string | null;
  academicComment?: string | null;
  behaviorComment?: string | null;
  recommendation?: string | null;
  overallRating?: string | null;
  publishedAt?: string | null;
  comments: Array<{
    authorName: string;
    content: string;
    createdAt: string;
  }>;
  sessionReviewStats?: SessionReviewStats;
}

const RATING_COLORS: Record<string, string> = {
  excellent: 'bg-brand-green-100 text-brand-green-700 dark:bg-brand-green-900/50 dark:text-brand-green-300',
  good: 'bg-[var(--color-info)]/10 text-[var(--color-info)] dark:bg-[var(--color-info-border)]/15 dark:text-[var(--color-info-border)]',
  average: 'bg-brand-orange-100 text-brand-orange-700 dark:bg-brand-orange-950/50 dark:text-brand-orange-300',
  needs_improvement: 'bg-destructive/10 text-destructive',
};
const RATING_LABEL: Record<string, string> = {
  excellent: 'Xuất sắc', good: 'Tốt',
  average: 'Khá', needs_improvement: 'Cần cải thiện',
};

interface PublicReportViewProps {
  report: PublicReport;
}

export function PublicReportView({ report }: PublicReportViewProps) {
  // Map public comments to PortalComment shape (no authorId needed for read-only)
  const comments: PortalComment[] = report.comments.map((c, i) => ({
    id: String(i),
    authorId: '',
    authorName: c.authorName,
    content: c.content,
    createdAt: c.createdAt,
    editedAt: null,
  }));

  // Set noindex + title without react-helmet-async
  useEffect(() => {
    const prev = document.title;
    document.title = 'Báo cáo tiến độ — CLC Education';
    let meta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      document.head.appendChild(meta);
    }
    meta.content = 'noindex';
    return () => {
      document.title = prev;
      meta?.parentNode?.removeChild(meta);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border bg-background py-3 px-4">
          <div className="max-w-2xl mx-auto flex items-center justify-center">
            <span className="text-lg font-bold text-primary">CLC Education</span>
          </div>
        </header>

        {/* Content */}
        <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          {/* Report identity */}
          <div className="space-y-1">
            <h1 className="text-xl font-bold">Báo cáo tiến độ học tập</h1>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <span>Học viên: <strong className="text-foreground">{report.studentName}</strong></span>
              <span>Lớp: <strong className="text-foreground">{report.className}</strong></span>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {report.periodLabel && (
                <span className="text-sm">Kỳ: {report.periodLabel}</span>
              )}
              {report.periodStart && report.periodEnd && (
                <span className="text-xs text-muted-foreground">
                  ({report.periodStart} → {report.periodEnd})
                </span>
              )}
              {report.overallRating && (
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${RATING_COLORS[report.overallRating] ?? ''}`}>
                  {RATING_LABEL[report.overallRating] ?? report.overallRating}
                </span>
              )}
            </div>
          </div>

          {/* Report content fields */}
          <div className="border border-border rounded-lg divide-y divide-border">
            {report.attendanceSummary && (
              <ReportSection label="Chuyên cần" value={report.attendanceSummary} />
            )}
            {report.academicComment && (
              <ReportSection label="Nhận xét học lực" value={report.academicComment} />
            )}
            {report.behaviorComment && (
              <ReportSection label="Nhận xét hành vi" value={report.behaviorComment} />
            )}
            {report.recommendation && (
              <ReportSection label="Khuyến nghị" value={report.recommendation} />
            )}
          </div>

          {/* Session review aggregate stats */}
          {report.sessionReviewStats && (
            <SessionReviewStatsCard stats={report.sessionReviewStats} />
          )}

          {/* Comments — read-only */}
          <ReportComments
            comments={comments}
            reportId=""
            readOnly
            currentUserId={undefined}
          />
        </main>

        {/* Footer */}
        <footer className="border-t border-border mt-8 py-4 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} CLC Education. Tất cả quyền được bảo lưu.
        </footer>
      </div>
  );
}

function ReportSection({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-4 py-3">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm text-foreground whitespace-pre-wrap">{value}</p>
    </div>
  );
}
